import { z } from "zod";

/** Bump only on breaking changes. Negotiated in the relay hello (Step 3). */
export const PROTOCOL_VERSION = 1;

const sessionId = z.string().min(1).max(64);
/** Terminal bytes, base64-encoded (JSON can't carry raw bytes). */
const bytes = z.string();

export const SessionInfo = z.object({
  id: sessionId,
  /** What is running, e.g. "claude". */
  command: z.string(),
  cwd: z.string(),
  startedAt: z.number().int(),
  cols: z.number().int().positive(),
  rows: z.number().int().positive(),
  status: z.enum(["running", "exited"]),
  /** Total bytes the session has ever produced; the tip of the output stream. */
  outputOffset: z.number().int().nonnegative(),
  /** The chat's name: what an agent session was asked first (or the title of the conversation it resumed). */
  title: z.string().max(200).optional(),
  /** When the session last produced output or was typed into (unix ms). Older computers don't send it. */
  lastActivityAt: z.number().int().optional(),
  /** The session's agent can also run as a chat (see `session.mode.set`). Older computers don't send it. */
  chat: z.boolean().optional(),
  /** Which way the agent runs right now: its terminal, or as a chat. Absent means "terminal". */
  mode: z.enum(["terminal", "chat"]).optional(),
});
export type SessionInfo = z.infer<typeof SessionInfo>;

export const SessionMode = z.enum(["terminal", "chat"]);
export type SessionMode = z.infer<typeof SessionMode>;

/**
 * One thing shown in a session's chat, as the agent's structured mode reports it.
 *  text      something the person or the agent said
 *  tool      the agent used a tool ("Edited src/x.ts", "Ran npm test"), with a short summary of what came back
 *  approval  the agent asks to do something; answered with `chat.approve` (its `id` is the request id)
 *  notice    a line from Brocoded itself ("Stopped", "Claude restarted")
 */
export const ChatEntry = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("text"), id: z.string().max(100), role: z.enum(["user", "assistant"]), text: z.string().max(60_000), at: z.number().int().optional() }),
  z.object({
    kind: z.literal("tool"),
    id: z.string().max(100),
    title: z.string().max(300),
    /** What the tool was given and what came back, cut short. */
    detail: z.string().max(4000).optional(),
    status: z.enum(["running", "done", "error"]),
    at: z.number().int().optional(),
  }),
  z.object({
    kind: z.literal("approval"),
    id: z.string().max(100),
    title: z.string().max(300),
    detail: z.string().max(8000).optional(),
    tool: z.string().max(100).optional(),
    /** How risky it looks (see `assessRisk`), worked out on the computer from what the agent was given. Older computers don't send it. */
    risk: z.object({ level: z.enum(["low", "check", "careful"]), headline: z.string().max(100) }).optional(),
    state: z.enum(["open", "approved", "denied", "expired"]),
    at: z.number().int().optional(),
  }),
  z.object({ kind: z.literal("notice"), id: z.string().max(100), text: z.string().max(1000), at: z.number().int().optional() }),
]);
export type ChatEntry = z.infer<typeof ChatEntry>;

/** What a chat's agent is doing: "starting" its process, "thinking", running a "tool", "idle" (waiting for the person), or stopped on an "error". */
export const ChatStatus = z.enum(["starting", "thinking", "tool", "idle", "error"]);
export type ChatStatus = z.infer<typeof ChatStatus>;

export const DeviceInfo = z.object({
  /** Fingerprint of the device's public key; stable, safe to show and to pass to revoke. */
  id: z.string().min(1).max(64),
  name: z.string(),
  pairedAt: z.number().int(),
  /** True for the device that asked. */
  self: z.boolean().optional(),
});
export type DeviceInfo = z.infer<typeof DeviceInfo>;

export const InboxEventKind = z.enum(["approval", "question", "completed", "failed"]);
export type InboxEventKind = z.infer<typeof InboxEventKind>;

/** One question an agent asked. It may offer options (then `multiSelect` says whether several may be chosen); any text is also a valid answer. */
export const FolderEntry = z.object({ name: z.string().max(255), path: z.string().max(4096) });
export type FolderEntry = z.infer<typeof FolderEntry>;

/** One earlier conversation with an agent in a folder, and the command that picks it up again. */
export const HistoryEntry = z.object({
  agent: z.string().max(32),
  id: z.string().max(100),
  title: z.string().max(300),
  updatedAt: z.number().int(),
  command: z.string().max(200),
  args: z.array(z.string().max(500)).max(10),
  /**
   * Set when this conversation is still open in a terminal on the computer (not started by Brocoded): "idle" = waiting
   * for the person, so it can be taken over; "busy" = working on a turn right now.
   */
  local: z.enum(["idle", "busy"]).optional(),
});
export type HistoryEntry = z.infer<typeof HistoryEntry>;

/** One line of an agent conversation, for reading earlier parts of a session. */
export const TranscriptItem = z.object({
  role: z.enum(["user", "assistant", "tool"]),
  text: z.string().max(8000),
  at: z.number().int().optional(),
});
export type TranscriptItem = z.infer<typeof TranscriptItem>;

/** One changed file in a folder's git working tree, with its patch (cut off if very large). */
export const DiffFile = z.object({
  path: z.string().max(4096),
  oldPath: z.string().max(4096).optional(),
  status: z.enum(["modified", "added", "deleted", "renamed", "untracked"]),
  added: z.number().int().nonnegative(),
  removed: z.number().int().nonnegative(),
  binary: z.boolean(),
  patch: z.string().max(120_000),
  /** The patch was cut short to keep the message small. */
  truncated: z.boolean(),
  /** Part of this file's change is staged (in the index) / not staged. Absent when comparing against a branch or a commit. */
  staged: z.boolean().optional(),
  unstaged: z.boolean().optional(),
});
export type DiffFile = z.infer<typeof DiffFile>;

/**
 * A branch or commit name sent by the phone. Strict on purpose: no leading "-" (it could be taken for an option), no "..",
 * no control characters. The computer also asks git (`check-ref-format`) before using one.
 */
export const GIT_REF_RE = /^(?!.*\.\.)(?!.*\/\/)(?!.*@\{)[A-Za-z0-9][A-Za-z0-9._/-]{0,119}$/;
const gitRef = z.string().regex(GIT_REF_RE);
const gitHash = z.string().regex(/^[0-9a-f]{7,40}$/);
const rid = z.string().min(1).max(40);

/** Coding agents that read skills (SKILL.md folders). */
export const SkillAgent = z.enum(["claude", "codex", "agy"]);
export type SkillAgent = z.infer<typeof SkillAgent>;
const skillName = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/);
/** What a phone can ask the skill library on a computer to do. */
export const SkillOp = z.discriminatedUnion("op", [
  z.object({ op: z.literal("list") }),
  /** What Brocoded publishes for everyone to import (the list comes with the reply as `catalog`). */
  z.object({ op: z.literal("catalog") }),
  /** Download one of Brocoded's published skills into the library. */
  z.object({ op: z.literal("install"), name: skillName }),
  /** Fetch skills from a git address (a GitHub repo, or a folder inside one) into the library. Every SKILL.md found is added. */
  z.object({ op: z.literal("add"), url: z.string().min(8).max(500) }),
  /** Make a library skill available to one agent (a link in that agent's skills folder). */
  z.object({ op: z.literal("enable"), name: skillName, agent: SkillAgent }),
  z.object({ op: z.literal("disable"), name: skillName, agent: SkillAgent }),
  /** Take a skill an agent already has (not from the library) into the library, so every agent can use it. */
  z.object({ op: z.literal("adopt"), name: skillName, agent: SkillAgent }),
  /** Delete a skill from the library (and the links to it). */
  z.object({ op: z.literal("remove"), name: skillName }),
]);
export type SkillOp = z.infer<typeof SkillOp>;
/** One skill: where it lives, and which agents can use it right now. */
export const SkillInfo = z.object({
  name: z.string().max(64),
  description: z.string().max(500),
  /** `library`: kept in the Brocoded skill library. `agent`: only inside an agent's own folder (can be adopted). */
  source: z.enum(["library", "agent"]),
  agents: z.object({ claude: z.boolean(), codex: z.boolean(), agy: z.boolean() }),
  /** Only when the request named a folder: which agents have this skill inside that folder (its own copy, for that project only). */
  project: z.object({ claude: z.boolean(), codex: z.boolean(), agy: z.boolean() }).optional(),
});
export type SkillInfo = z.infer<typeof SkillInfo>;
/** A skill Brocoded publishes: importable with one tap. */
export const CatalogSkill = z.object({ name: z.string().max(64), description: z.string().max(500), files: z.number().int().min(1).max(500), bytes: z.number().int().min(0) });
export type CatalogSkill = z.infer<typeof CatalogSkill>;
const repoPath = z.string().min(1).max(4096);

/** Where the current branch stands, sent with a diff so the Changes screen can offer Push and Undo. */
export const GitInfo = z.object({
  /** "origin/main", when the branch tracks something. */
  upstream: z.string().max(300).optional(),
  ahead: z.number().int().nonnegative(),
  behind: z.number().int().nonnegative(),
  /** There is an `origin` remote (so a branch with no upstream can be pushed with -u). */
  hasOrigin: z.boolean(),
  /** The last commit exists only here (no remote branch has it), so it can be undone. */
  canUndo: z.boolean(),
  lastSubject: z.string().max(300).optional(),
});
export type GitInfo = z.infer<typeof GitInfo>;

export const GitBranch = z.object({
  /** "main", or "origin/main" for a remote branch. */
  name: z.string().max(300),
  remote: z.boolean(),
  current: z.boolean(),
  subject: z.string().max(300),
  /** Seconds since 1970 of the branch's last commit. */
  at: z.number().int().nonnegative(),
  upstream: z.string().max(300).optional(),
  ahead: z.number().int().nonnegative().optional(),
  behind: z.number().int().nonnegative().optional(),
  /** The upstream branch is gone from the remote. */
  gone: z.boolean().optional(),
});
export type GitBranch = z.infer<typeof GitBranch>;

export const GitCommit = z.object({
  hash: z.string().max(40),
  short: z.string().max(12),
  subject: z.string().max(300),
  author: z.string().max(200),
  at: z.number().int().nonnegative(),
  /** Parent commit hashes (what the history graph draws lines to); absent from an older computer. */
  parents: z.array(z.string().max(40)).max(16).optional(),
  /** Branch and tag names that point at this commit (`main`, `origin/main`, `v1.0`). */
  refs: z.array(z.string().max(200)).max(20).optional(),
});
export type GitCommit = z.infer<typeof GitCommit>;

/** One thing to do to the repository. `paths` are exactly as the Changes list shows them; `all` stages or unstages everything. */
export const GitOp = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("stage"), paths: z.array(repoPath).max(200).optional(), all: z.boolean().optional() }),
  z.object({ kind: z.literal("unstage"), paths: z.array(repoPath).max(200).optional(), all: z.boolean().optional() }),
  z.object({ kind: z.literal("discard"), paths: z.array(repoPath).min(1).max(200) }),
  z.object({ kind: z.literal("commit"), message: z.string().min(1).max(5000), stageAll: z.boolean().optional() }),
  z.object({ kind: z.literal("switch"), branch: gitRef }),
  z.object({ kind: z.literal("create"), branch: gitRef }),
  z.object({ kind: z.literal("push") }),
  z.object({ kind: z.literal("undoCommit") }),
]);
export type GitOp = z.infer<typeof GitOp>;

/** How a git request ended: "ok" when the folder is a git repo and git answered; otherwise `message` says why. */
export const GitState = z.enum(["ok", "not_repo", "git_missing", "no_folder", "error"]);
export type GitState = z.infer<typeof GitState>;

/** How a computer is kept reachable. Set from the phone, applied (and confirmed) by the computer itself. */
export const PowerSettings = z.object({
  /** Keep the computer from idle-sleeping while it is plugged in. */
  keepAwake: z.boolean(),
  /** Also keep it awake on battery. Off by default: it drains the battery. */
  onBattery: z.boolean(),
  /** Stop keeping awake after this many minutes (0: until turned off). */
  timeoutMin: z.number().int().min(0).max(1440),
});
export type PowerSettings = z.infer<typeof PowerSettings>;

export const DEFAULT_POWER_SETTINGS: PowerSettings = { keepAwake: false, onBattery: false, timeoutMin: 0 };

/** A MAC address, six hex pairs with ":" or "-". */
export const MAC_RE = /^[0-9a-f]{2}([:-][0-9a-f]{2}){5}$/i;

/**
 * What the computer says about its own power handling. `active` is what the computer confirms is happening right now:
 * a phone never shows a setting as applied until this says so.
 */
export const PowerState = z.object({
  settings: PowerSettings,
  os: z.enum(["mac", "windows", "linux", "other"]),
  /** This computer can hold itself awake (Mac and Windows). */
  supported: z.boolean(),
  hasBattery: z.boolean().nullable(),
  /** Running on battery right now; null when unknown or there is no battery. */
  onBattery: z.boolean().nullable(),
  /** The computer is being held awake right now. */
  active: z.boolean(),
  /** Why it is or is not: "active", "off", "on_battery" (waiting for power), "timeout" (the time ran out), "failed" (the system refused), "unsupported". */
  reason: z.enum(["active", "off", "on_battery", "timeout", "failed", "unsupported"]),
  /** When the timeout ends (unix ms), while it counts down. */
  until: z.number().int().nullable(),
  wake: z.object({
    /** Address of this computer's network card, so another computer of the same person on the same network can wake it. Only ever sent through the encrypted channel to paired phones. */
    mac: z.string().regex(MAC_RE).nullable(),
    /** Where on the local network that packet goes (the subnet's broadcast address). */
    broadcast: z.string().max(45).nullable(),
    /** Is the computer set up to wake on a network packet? null: could not tell. */
    enabled: z.boolean().nullable(),
  }),
});
export type PowerState = z.infer<typeof PowerState>;

export const InboxQuestion = z.object({
  question: z.string().max(1000),
  header: z.string().max(100).optional(),
  options: z.array(z.object({ label: z.string().max(500), description: z.string().max(1000).optional() })).max(20).optional(),
  multiSelect: z.boolean().optional(),
});
export type InboxQuestion = z.infer<typeof InboxQuestion>;

/** How much of one limit an agent's plan has used, and when it starts over. */
export const UsageWindow = z.object({
  /** "Session (5 hours)", "Week", "Gemini · week". */
  label: z.string().max(60),
  usedPercent: z.number().min(0).max(100),
  /** When it starts over (unix ms); absent when the agent doesn't say. */
  resetsAt: z.number().int().optional(),
});
export type UsageWindow = z.infer<typeof UsageWindow>;

export const AgentUsage = z.object({
  agent: z.enum(["claude-code", "codex", "antigravity"]),
  /** `not_installed`: no such program on this computer. `unavailable`: it is there, but didn't give limits (not signed in, an API key instead of a plan, a changed screen). */
  status: z.enum(["ok", "not_installed", "unavailable"]),
  windows: z.array(UsageWindow).max(8),
  note: z.string().max(200).optional(),
});
export type AgentUsage = z.infer<typeof AgentUsage>;

/**
 * A standing answer to an approval, kept on the computer so it works while the phone is away.
 *  allow  approve without asking; deny  refuse without asking (a deny always beats an allow).
 * It matches by what the agent asked for: `tool` ("Bash", "Edit", ...; "*" is any tool) and `prefix`. For a command, `prefix`
 * is the start of the command (a whole word, never a part of one); for a file, the start of its path; empty means any use of the tool.
 */
export const ApprovalRule = z.object({
  id: z.string().min(1).max(64),
  effect: z.enum(["allow", "deny"]),
  tool: z.string().min(1).max(100),
  prefix: z.string().max(500),
  /** What the person sees in the list ("npm test", "Edits in src/"). */
  label: z.string().max(200),
  createdAt: z.number().int(),
});
export type ApprovalRule = z.infer<typeof ApprovalRule>;

/**
 * Something an agent needs from, or wants to tell, the human. Produced deterministically from the agent's own
 * signals (never by reading the screen), and always tied to the session it came from.
 *  approval   the agent wants permission (tool + what it will do); answer with approve / deny
 *  question   the agent asked something; answer with text (or one of `options`)
 *  completed  the agent finished its turn
 *  failed     the agent hit an error, or its process exited with an error
 */
export const InboxEvent = z.object({
  id: z.string().min(1).max(64),
  sessionId: z.string().min(1).max(64),
  /** Which agent adapter produced it, e.g. "claude-code". */
  agent: z.string().min(1).max(32),
  kind: InboxEventKind,
  title: z.string().max(300),
  /** The specifics: the command or file for an approval, the question text, the error message. */
  detail: z.string().max(8000).optional(),
  /** Approval: the tool asking, e.g. "Bash". */
  tool: z.string().max(100).optional(),
  /** Question: what was asked (an agent can ask up to a few at once); answer them all, in order. */
  questions: z.array(InboxQuestion).min(1).max(10).optional(),
  /** The agent is running somewhere other than a Brocoded session (a Terminal or editor on the computer): there is no terminal here to open. */
  external: z.boolean().optional(),
  /** The folder the agent is working in (only for external sessions, where there is no session to name it). */
  cwd: z.string().max(4096).optional(),
  createdAt: z.number().int(),
  /** `open` until acted on (approvals/questions) or dismissed (completed/failed). */
  state: z.enum(["open", "resolved"]),
  /**
   * How it was resolved. "handled-elsewhere" = someone or something at the computer got there first (answered the
   * dialog, sent a new prompt, the turn ended). "expired" = nobody can answer it any more (the session ended, or the
   * agent stopped waiting for us).
   */
  outcome: z.enum(["approved", "denied", "answered", "handled-elsewhere", "expired", "dismissed"]).optional(),
  /** Set when one of the person's approval rules decided it (the rule's label), so Activity can say "Allowed by your rule". */
  rule: z.string().max(200).optional(),
  /** Approvals: how risky it looks and what it will do, in a few words (see `assessRisk`). Older computers don't send it; the phone works it out. */
  risk: z.object({ level: z.enum(["low", "check", "careful"]), headline: z.string().max(100) }).optional(),
});
export type InboxEvent = z.infer<typeof InboxEvent>;

/**
 * Messages that travel *inside* the encrypted channel. The relay never sees them.
 * Discriminated on `t`. New message types are additive; existing ones don't change
 * within a protocol version.
 */
/** One agent on a team (an "office" run by the orchestrator on this computer). */
export const OfficeAgent = z.object({
  id: z.string().min(1).max(128),
  name: z.string().max(100),
  role: z.string().max(200).optional(),
  /** Which agent program it runs ("claude", "codex", ...). */
  provider: z.string().max(40),
  /** The orchestrator that splits work and hands it out. */
  isGod: z.boolean(),
  archived: z.boolean(),
  /** The person paused it from delivering new work. */
  onHold: z.boolean(),
  /** The runaway guard's verdict: healthy, or how far it has stepped in. */
  breaker: z.enum(["healthy", "steering", "constrained", "stopped"]),
  tokens: z.number().nonnegative(),
  usd: z.number().nonnegative(),
  lastTool: z.string().max(100).nullable(),
  lastActiveSecAgo: z.number().int().nonnegative().nullable(),
  /** Messages waiting for it that it hasn't read. */
  inboxBacklog: z.number().int().nonnegative(),
});
export type OfficeAgent = z.infer<typeof OfficeAgent>;

export const OfficeTask = z.object({
  id: z.string().min(1).max(128),
  title: z.string().max(300),
  /** "todo", "doing", "blocked" or "done" (kept a string so a newer orchestrator can add states). */
  status: z.string().max(20),
  assignee: z.string().max(128).optional(),
  description: z.string().max(2000).optional(),
  /** Ids of the steps this one waits for. */
  dependsOn: z.array(z.string().max(128)).max(20).optional(),
});
export type OfficeTask = z.infer<typeof OfficeTask>;

export const OfficeActivityKind = z.enum(["start", "prompt", "edit", "command", "tool", "stop", "waiting", "error"]);
export type OfficeActivityKind = z.infer<typeof OfficeActivityKind>;

/** One thing an agent did. `id` sorts by time and is the paging cursor. */
export const OfficeActivity = z.object({
  id: z.string().min(1).max(40),
  ts: z.number().int(),
  agentId: z.string().min(1).max(128),
  sessionId: z.string().max(100).optional(),
  kind: OfficeActivityKind,
  /** One short line, ready to show as a list row. */
  title: z.string().max(200),
  /** The command or the instruction, when there is one. */
  detail: z.string().max(2000).optional(),
  tool: z.string().max(100).optional(),
  file: z.string().max(1000).optional(),
});
export type OfficeActivity = z.infer<typeof OfficeActivity>;

/** One message the team passed on: a step handed to an agent, a report back, or an answer to the person ("human"). */
export const OfficeMessage = z.object({
  id: z.string().min(1).max(100),
  ts: z.number().int(),
  from: z.string().min(1).max(128),
  to: z.string().min(1).max(128),
  act: z.string().max(20),
  subject: z.string().max(200),
  body: z.string().max(4000),
});
export type OfficeMessage = z.infer<typeof OfficeMessage>;

export const OfficeSummary = z.object({
  agentId: z.string().max(128),
  total: z.number().int().nonnegative(),
  firstTs: z.number().int().nullable(),
  lastTs: z.number().int().nullable(),
  filesEdited: z.array(z.string().max(1000)).max(100),
  commands: z.number().int().nonnegative(),
  errors: z.number().int().nonnegative(),
  prompts: z.number().int().nonnegative(),
});
export type OfficeSummary = z.infer<typeof OfficeSummary>;

export const Message = z.discriminatedUnion("t", [
  // client -> daemon
  /** The only message an unpaired device may send; `token` is the one-time pairing token (base64url). */
  z.object({ t: z.literal("pair.request"), token: z.string().min(1).max(64), name: z.string().min(1).max(64) }),
  /** The phone is signed in to a Brocoded account and hands that sign-in to this computer, which links itself to the account. */
  z.object({ t: z.literal("account.attach"), token: z.string().min(1).max(100) }),
  /** Is this computer signed in to an account? Answered with a masked hint only. */
  z.object({ t: z.literal("account.get") }),
  /** The phone is signing out: it asks this computer to sign out of the same account (the token proves it is that account). */
  z.object({ t: z.literal("account.detach"), token: z.string().min(1).max(100) }),
  z.object({ t: z.literal("devices.list") }),
  z.object({ t: z.literal("devices.revoke"), deviceId: z.string().min(1).max(64) }),
  z.object({ t: z.literal("session.list") }),
  /** Stop a running session's process (the session stays listed as ended). */
  z.object({ t: z.literal("session.end"), sessionId: z.string().min(1).max(64) }),
  /** Forget an ended session (refused while it is still running). The reply is the fresh `session.listed`. */
  z.object({ t: z.literal("session.remove"), sessionId: z.string().min(1).max(64) }),
  z.object({ t: z.literal("inbox.list") }),
  /** What has changed (git) in the folder a session runs in. Read-only. Answered with `git.diffed`. */
  z.object({ t: z.literal("git.diff"), sessionId: z.string().min(1).max(64), /** Compare against merge-base(<against>, HEAD) instead of HEAD. */ against: gitRef.optional() }),
  /** Branches of the session's folder with ahead/behind (no network). Answered with `git.branched`. */
  z.object({ t: z.literal("git.branches"), sessionId: z.string().min(1).max(64), rid }),
  /** A page of the commit log. Answered with `git.logged`. */
  z.object({ t: z.literal("git.log"), sessionId: z.string().min(1).max(64), rid, skip: z.number().int().min(0).max(100_000).default(0), limit: z.number().int().min(1).max(50).default(30) }),
  /** One commit's files and patches. Answered with `git.shown`. */
  z.object({ t: z.literal("git.show"), sessionId: z.string().min(1).max(64), rid, hash: gitHash }),
  /** Change the repository (stage, commit, push, ...). Risky ones are refused with code "busy" while the agent is mid-turn unless `ignoreBusy`. Answered with `git.done`. */
  z.object({ t: z.literal("git.do"), sessionId: z.string().min(1).max(64), rid, op: GitOp, ignoreBusy: z.boolean().optional() }),
  /** Folders (never files) inside `path`; with no path, the home folder plus recent projects. */
  z.object({ t: z.literal("fs.list"), path: z.string().min(1).max(4096).optional() }),
  /** Find folders (never files) by name anywhere under `root` (default: the home folder), like the search box in Explorer or Finder. Answered with `fs.found`. */
  z.object({ t: z.literal("fs.find"), rid, query: z.string().min(1).max(100), root: z.string().min(1).max(4096).optional() }),
  /** Files and folders in a session's own folder whose path matches `query` (empty: the top level), for `@file` mentions in Compose. Answered with `files.found`. */
  z.object({ t: z.literal("files.find"), sessionId, rid, query: z.string().max(100) }),
  /**
   * Earlier messages of a session's conversation, newest page first. `before` is the cursor a previous reply gave (omit it
   * for the latest). Read-only; answered with `session.transcripted`.
   */
  z.object({
    t: z.literal("session.transcript"),
    sessionId: z.string().min(1).max(64),
    before: z.number().int().nonnegative().optional(),
    /** Instead of a page: only what was written after this point (an `end` from an earlier reply), for following a chat live. */
    after: z.number().int().nonnegative().optional(),
    limit: z.number().int().min(1).max(100).default(30),
  }),
  /** Earlier agent conversations in a folder (newest first), so one can be resumed. Read-only. */
  z.object({ t: z.literal("agent.history"), cwd: z.string().min(1).max(4096) }),
  /**
   * Act on an open event. A question needs `answers`: one text per question, in order (for a multi-select question,
   * the chosen labels joined with ", "). Ignored for approvals.
   */
  z.object({
    t: z.literal("inbox.respond"),
    eventId: z.string().min(1).max(64),
    action: z.enum(["approve", "deny", "answer"]),
    answers: z.array(z.string().max(4000)).max(10).optional(),
  }),
  /** Remove a card (finished/failed notices, or anything already resolved) from the inbox. */
  z.object({ t: z.literal("inbox.dismiss"), eventId: z.string().min(1).max(64) }),
  /** Skills (SKILL.md folders) kept on this computer for the coding agents. Answered with `skills.state`. */
  z.object({ t: z.literal("skills.do"), rid, op: SkillOp, /** A folder (usually a repo): `enable` / `disable` then act on that folder only, and the reply says what it has (`project`). */ cwd: z.string().min(1).max(4096).optional() }),
  /** The approval rules kept on this computer. Answered with `rules.listed`. */
  z.object({ t: z.literal("rules.get") }),
  /** Add a rule (for "Always allow"). Answered with `rules.listed`, to every phone. */
  z.object({ t: z.literal("rules.add"), rule: ApprovalRule.omit({ id: true, createdAt: true }) }),
  z.object({ t: z.literal("rules.remove"), id: z.string().min(1).max(64) }),
  /** How much of each agent's plan limits is used, read on this computer. `refresh` asks for a fresh reading instead of a recent one. Answered with `usage.state`. */
  z.object({ t: z.literal("usage.get"), refresh: z.boolean().optional() }),
  /** The team on this computer: its agents and tasks. Answered with `office.state`. */
  z.object({ t: z.literal("office.get") }),
  /** What agents have done, newest first. `before` is the cursor from the previous page. Answered with `office.history.listed`. */
  z.object({
    t: z.literal("office.history"),
    rid,
    agentId: z.string().min(1).max(128).optional(),
    before: z.string().max(40).optional(),
    limit: z.number().int().min(1).max(200).optional(),
    kinds: z.array(OfficeActivityKind).max(8).optional(),
  }),
  /** A recap of one agent's work. Answered with `office.summary.shown`. */
  z.object({ t: z.literal("office.summary"), rid, agentId: z.string().min(1).max(128) }),
  /** Give the team a goal. The orchestrator splits it up. Answered with `office.done`. */
  z.object({ t: z.literal("office.send"), rid, body: z.string().min(1).max(8000), subject: z.string().max(200).optional() }),
  /** Pause, resume, stop or steer one agent. Answered with `office.done`. */
  z.object({
    t: z.literal("office.control"),
    rid,
    agentId: z.string().min(1).max(128),
    action: z.enum(["pause", "resume", "halt", "steer"]),
    text: z.string().max(4000).optional(),
  }),
  /** The latest messages the team passed on, oldest first. Answered with `office.messages.listed`. */
  z.object({ t: z.literal("office.messages"), rid, limit: z.number().int().min(1).max(100).optional() }),
  /** The agent's screen as text. Answered with `office.terminal.shown`. */
  z.object({ t: z.literal("office.terminal"), rid, agentId: z.string().min(1).max(128) }),
  /** Show or hide the orchestrator's own window on the computer. Answered with `office.done`. */
  z.object({ t: z.literal("office.window"), rid, visible: z.boolean() }),
  /** Start or stop the team on this computer (the choice is remembered). Answered with `office.done`, then a fresh `office.state`. */
  z.object({ t: z.literal("office.engine"), rid, action: z.enum(["start", "stop"]) }),
  /** Type into an agent's terminal. Answered with `office.done`. */
  z.object({ t: z.literal("office.input"), rid, agentId: z.string().min(1).max(128), text: z.string().min(1).max(4000), enter: z.boolean().optional() }),
  /** Is voice input ready on this computer (a speech model and a way to read the recording)? Answered with `voice.state`. */
  z.object({ t: z.literal("voice.get") }),
  /**
   * A short recording to turn into text on this computer, so the audio never reaches anyone else. `rid` comes back in the reply.
   * `audio` is base64; `mime` is what the phone's recorder made ("audio/webm", "audio/mp4", ...).
   */
  z.object({
    t: z.literal("voice.transcribe"),
    rid,
    mime: z.string().min(1).max(100),
    audio: bytes,
    /**
     * A long recording is sent in pieces: `seq` counts from 0 and the piece with `last: true` ends it. Without either, `audio` is the
     * whole recording. The phone sends `audio/pcm;rate=16000` (16-bit little-endian mono) so the computer needs no audio converter.
     */
    seq: z.number().int().min(0).max(15).optional(),
    last: z.boolean().optional(),
  }),
  /** Get voice ready on this computer: install the speech program if it is missing (Mac with Homebrew) and download the speech model (about 150 MB). Progress comes back as `voice.setup.progress`, then `voice.state`. */
  z.object({ t: z.literal("voice.setup") }),
  /**
   * A photo from the phone, saved on this computer so the agent can open it by path. `data` is base64 of a JPEG, PNG, WebP or GIF;
   * a large one is sent in pieces (`seq` from 0, `last: true` on the final piece), like a voice recording. `rid` comes back in `image.saved`.
   */
  z.object({
    t: z.literal("image.upload"),
    rid,
    mime: z.string().min(1).max(100),
    data: bytes,
    seq: z.number().int().min(0).max(15).optional(),
    last: z.boolean().optional(),
  }),
  z.object({
    t: z.literal("session.create"),
    command: z.string().min(1),
    args: z.array(z.string()).default([]),
    cwd: z.string().optional(),
    cols: z.number().int().min(1).max(1000).default(80),
    rows: z.number().int().min(1).max(1000).default(24),
    /** Name for the session, e.g. the title of the conversation being resumed. */
    title: z.string().max(200).optional(),
    /**
     * Continue a conversation that is open (and idle) in a terminal on the computer: the computer ends that terminal's
     * process and resumes the same conversation here, so nothing runs twice. `command`/`args` are ignored.
     */
    takeOver: z.string().min(1).max(100).optional(),
    /** With `takeOver`: also when the chat is in the middle of a turn (that turn is cut off). Only after the person confirmed. */
    force: z.boolean().optional(),
  }),
  /** Start receiving a session's output, replaying everything from `fromOffset`. */
  z.object({
    t: z.literal("session.attach"),
    sessionId,
    fromOffset: z.number().int().nonnegative(),
    /**
     * The client can draw a `snapshot`. If it is far behind (or what it wants was evicted), the computer then sends the
     * current screen instead of replaying a long stretch of raw output.
     */
    snapshot: z.boolean().optional(),
  }),
  z.object({ t: z.literal("input"), sessionId, data: bytes }),
  z.object({ t: z.literal("resize"), sessionId, cols: z.number().int().positive(), rows: z.number().int().positive() }),

  /**
   * Run the session's agent as a chat, or back in its terminal. The same conversation carries on; only allowed between turns.
   * Answered (and told to every phone) with `session.mode`; refused with `session.mode` carrying a `reason`.
   */
  z.object({ t: z.literal("session.mode.set"), sessionId, mode: SessionMode }),
  /** Start following a session's chat: answered with `chat.state`, then live `chat.*` messages. */
  z.object({ t: z.literal("chat.attach"), sessionId }),
  z.object({ t: z.literal("chat.detach"), sessionId }),
  /** Something the person typed in the chat. Refused (with `error`) while the agent is still replying. */
  z.object({ t: z.literal("chat.send"), sessionId, text: z.string().min(1).max(20_000) }),
  /** Stop the turn the agent is working on. */
  z.object({ t: z.literal("chat.interrupt"), sessionId }),
  /** Allow or deny what the agent asked in a chat `approval` entry. */
  /** Turn "allow everything in this chat" on or off: while on, every approval the agent asks for here is allowed without asking (anything careful too). It ends with the chat. Told to every phone with `chat.allowAll`. */
  z.object({ t: z.literal("chat.allowAll.set"), sessionId, on: z.boolean() }),
  z.object({ t: z.literal("chat.approve"), sessionId, reqId: z.string().min(1).max(100), allow: z.boolean() }),

  /** How this computer is kept reachable. Answered with `power.state`. */
  z.object({ t: z.literal("power.get") }),
  /** Change how this computer is kept reachable. Answered with `power.state` once the computer has applied it. */
  z.object({ t: z.literal("power.set"), settings: PowerSettings }),
  /** Ask this computer to send a wake-on-LAN packet to another computer on its network. Answered with `power.wake.sent`. */
  z.object({ t: z.literal("power.wake"), mac: z.string().regex(MAC_RE), broadcast: z.string().max(45) }),

  /** This phone's push address (from the browser's push service). The computer uses it to wake the phone when something needs it. */
  z.object({
    t: z.literal("push.subscribe"),
    endpoint: z.string().min(1).max(2048),
    /** Web Push only: the subscription's keys, so the relay can encrypt a payload for the browser. */
    keys: z.object({ p256dh: z.string().min(1).max(200), auth: z.string().min(1).max(100) }).optional(),
    /** The person opted in to alerts that say which session it is (agent, folder, title), instead of fixed wording. */
    details: z.boolean().optional(),
    /** `false`: don't wake this phone when an agent finishes. */
    finished: z.boolean().optional(),
    /** Quiet hours (minutes after midnight in the phone's time zone): only approvals and questions wake the phone then. */
    quiet: z.object({ from: z.number().int().min(0).max(1439), to: z.number().int().min(0).max(1439), tz: z.string().min(1).max(64) }).optional(),
  }),
  z.object({ t: z.literal("push.unsubscribe") }),

  // daemon -> client
  z.object({ t: z.literal("pair.accepted"), deviceId: z.string() }),
  /** The code was right; now the person at the computer has to allow this phone. Sent before `pair.accepted`/`pair.rejected`. */
  z.object({ t: z.literal("pair.pending") }),
  /** One reason for every failure (wrong, expired, used up): don't tell a guesser which. */
  z.object({ t: z.literal("pair.rejected") }),
  /** `hint` is masked (a***@gmail.com): a paired phone learns that the computer is signed in, never whose full address. */
  z.object({ t: z.literal("account.state"), signedIn: z.boolean(), hint: z.string().max(100).nullable(), plan: z.enum(["guest", "free", "pro"]).optional() }),
  z.object({ t: z.literal("account.attached"), email: z.string().max(320), name: z.string().max(200).nullable() }),
  z.object({ t: z.literal("power.state"), state: PowerState }),
  /** `ok: false` says the packet could not be sent from here (not a promise that the other computer woke up). */
  z.object({ t: z.literal("power.wake.sent"), mac: z.string().max(17), ok: z.boolean(), message: z.string().max(300).optional() }),
  z.object({ t: z.literal("devices.listed"), devices: z.array(DeviceInfo) }),
  z.object({ t: z.literal("devices.revoked"), deviceId: z.string() }),
  z.object({ t: z.literal("session.created"), session: SessionInfo }),
  /** Subfolders of `path` (hidden ones left out), sorted. `recent` comes only with the home listing. */
  z.object({
    t: z.literal("fs.listed"),
    path: z.string().max(4096),
    parent: z.string().max(4096).nullable(),
    dirs: z.array(FolderEntry).max(1000),
    truncated: z.boolean(),
    recent: z.array(FolderEntry).max(30).optional(),
    /** Common places (Desktop, Documents, ...), with the home listing only. */
    places: z.array(FolderEntry).max(30).optional(),
  }),
  /** Paths (relative to the session's folder; folders end with "/") that match a `files.find`, best first. */
  z.object({ t: z.literal("files.found"), rid, results: z.array(z.string().max(500)).max(40), truncated: z.boolean() }),
  /** Folders whose name matches a `fs.find`, nearest first. `truncated`: there were more, or the search stopped early to stay quick. */
  z.object({
    t: z.literal("fs.found"),
    rid,
    root: z.string().max(4096),
    results: z.array(FolderEntry).max(60),
    truncated: z.boolean(),
    /** The search couldn't run (folder gone, or the computer refused it); `denied` says it was a permission refusal. */
    error: z.string().max(500).optional(),
    denied: z.boolean().optional(),
  }),
  /**
   * Reply to `git.diff`. `state` is "ok" when the folder is a git repo (even with no changes); otherwise `message` says why.
   * `truncated`: more files changed than are listed.
   */
  z.object({
    t: z.literal("git.diffed"),
    sessionId: z.string().max(64),
    state: z.enum(["ok", "not_repo", "git_missing", "no_folder", "error"]),
    message: z.string().max(500).optional(),
    branch: z.string().max(300).optional(),
    files: z.array(DiffFile).max(200),
    truncated: z.boolean(),
    /** Where the branch stands (newer computers only). */
    info: GitInfo.optional(),
    /** Echo of `against` when the diff is a comparison. */
    against: z.string().max(200).optional(),
  }),
  z.object({
    t: z.literal("git.branched"),
    sessionId: z.string().max(64),
    rid,
    state: GitState,
    message: z.string().max(500).optional(),
    /** null: HEAD is detached. */
    current: z.string().max(300).nullable().optional(),
    branches: z.array(GitBranch).max(300),
    truncated: z.boolean(),
  }),
  z.object({
    t: z.literal("git.logged"),
    sessionId: z.string().max(64),
    rid,
    state: GitState,
    message: z.string().max(500).optional(),
    commits: z.array(GitCommit).max(50),
    more: z.boolean(),
  }),
  z.object({
    t: z.literal("git.shown"),
    sessionId: z.string().max(64),
    rid,
    state: GitState,
    message: z.string().max(500).optional(),
    commit: GitCommit.extend({ body: z.string().max(4000) }).optional(),
    files: z.array(DiffFile).max(200),
    truncated: z.boolean(),
  }),
  z.object({
    t: z.literal("git.done"),
    sessionId: z.string().max(64),
    rid,
    ok: z.boolean(),
    /** Why not: "busy", "auth", "rejected", "dirty_switch", "nothing_to_commit", "no_identity", ... */
    code: z.string().max(40).optional(),
    message: z.string().max(1000).optional(),
    /** git's own output, for "Show details". */
    details: z.string().max(8000).optional(),
  }),
  /** Reply to `session.transcript`: `items` oldest first; `before` is the cursor for the page before them, null at the start. */
  z.object({
    t: z.literal("session.transcripted"),
    sessionId: z.string().max(64),
    items: z.array(TranscriptItem).max(100),
    before: z.number().int().nonnegative().nullable(),
    /** Where the conversation ended as of this reply: ask for `after` this to get only what comes next. */
    end: z.number().int().nonnegative().optional(),
    /** The conversation file was replaced by a shorter one: what the phone shows is stale. */
    reset: z.boolean().optional(),
    /** For a chat open in a terminal on the computer: working on a turn, or waiting for the person. */
    local: z.enum(["idle", "busy"]).optional(),
  }),
  /** Reply to `agent.history`; `cwd` is echoed exactly as asked. */
  z.object({ t: z.literal("agent.history.listed"), cwd: z.string().max(4096), items: z.array(HistoryEntry).max(50) }),
  /** The whole inbox, newest first. Sent in reply to `inbox.list` and to every phone when it connects. */
  z.object({ t: z.literal("inbox.listed"), events: z.array(InboxEvent) }),
  /** A new event, or an existing one that changed (for example, resolved elsewhere). Pushed to every connected phone. */
  z.object({ t: z.literal("inbox.event"), event: InboxEvent }),
  z.object({ t: z.literal("inbox.removed"), eventId: z.string() }),
  z.object({ t: z.literal("usage.state"), agents: z.array(AgentUsage).max(5), /** When it was read (unix ms). */ at: z.number().int(), /** False while individual agents are still being checked. */ complete: z.boolean().optional() }),
  /** Reply to `skills.do`: every skill now known, and `message` (the reason when `ok` is false, else what was done). */
  z.object({ t: z.literal("skills.state"), rid, ok: z.boolean(), message: z.string().max(500).optional(), skills: z.array(SkillInfo).max(500), /** Only for the `catalog` op: what Brocoded publishes. */ catalog: z.array(CatalogSkill).max(500).optional(), /** Where the library lives on the computer. */ library: z.string().max(4096) }),
  z.object({ t: z.literal("rules.listed"), rules: z.array(ApprovalRule).max(200) }),
  /**
   * The team as it is now. `available: false` says why in `reason` (the orchestrator isn't running, or isn't installed).
   * `windowVisible`: the orchestrator's own window is open on the computer (null when it can't tell).
   */
  z.object({
    t: z.literal("office.state"),
    available: z.boolean(),
    reason: z.string().max(500).optional(),
    agents: z.array(OfficeAgent).max(100),
    tasks: z.array(OfficeTask).max(500),
    windowVisible: z.boolean().nullable(),
    at: z.number().int(),
    /** Whether the computer can run a team and where that stands. Absent on computers that predate it. */
    engine: z.enum(["unavailable", "stopped", "starting", "running", "failed"]).optional(),
    engineMessage: z.string().max(300).optional(),
  }),
  z.object({ t: z.literal("office.history.listed"), rid, entries: z.array(OfficeActivity).max(200), nextBefore: z.string().max(40).nullable() }),
  z.object({ t: z.literal("office.summary.shown"), rid, summary: OfficeSummary }),
  /** Pushed to every phone as an agent does something. */
  z.object({ t: z.literal("office.activity"), entry: OfficeActivity }),
  /** Pushed to every phone as the team passes a message on. */
  z.object({ t: z.literal("office.message"), message: OfficeMessage }),
  z.object({ t: z.literal("office.messages.listed"), rid, messages: z.array(OfficeMessage).max(100) }),
  z.object({ t: z.literal("office.terminal.shown"), rid, ok: z.boolean(), text: z.string().max(8000).optional(), idleMs: z.number().int().nonnegative().nullable().optional(), message: z.string().max(500).optional() }),
  z.object({ t: z.literal("office.done"), rid, ok: z.boolean(), message: z.string().max(500).optional() }),
  /** `ready: false` says why in `reason` (shown to the person with what to do). `limitSeconds`: the longest recording it takes. */
  z.object({
    t: z.literal("voice.state"),
    ready: z.boolean(),
    reason: z.string().max(500).optional(),
    limitSeconds: z.number().int().positive().optional(),
    /** What is missing: `model` can be fetched by the app itself (`voice.setup`); `program` (the speech program) can't. Older computers don't say. */
    needs: z.enum(["model", "program"]).optional(),
    /** A `voice.setup` is under way on this computer right now. */
    settingUp: z.boolean().optional(),
  }),
  /** Where `voice.setup` is. `percent` only while downloading. `error` says why in `message`. */
  z.object({ t: z.literal("voice.setup.progress"), stage: z.enum(["installing", "downloading", "checking", "done", "error"]), percent: z.number().int().min(0).max(100).optional(), message: z.string().max(500).optional() }),
  z.object({ t: z.literal("voice.transcribed"), rid, ok: z.boolean(), text: z.string().max(8000).optional(), message: z.string().max(500).optional() }),
  /** The answer to `image.upload`: `path` is where the picture now is on this computer (absolute), to mention in a message to the agent. */
  z.object({ t: z.literal("image.saved"), rid, ok: z.boolean(), path: z.string().max(1000).optional(), message: z.string().max(500).optional() }),
  z.object({ t: z.literal("session.listed"), sessions: z.array(SessionInfo) }),
  /** `offset` is the byte position of the first byte in `data` within the session's output stream. */
  z.object({ t: z.literal("output"), sessionId, offset: z.number().int().nonnegative(), data: bytes }),
  /**
   * The session's screen as it is now, as bytes that begin with a terminal reset and redraw it. `offset` is where in the
   * output stream the screen ends: live `output` follows from there, and the client treats it as its position.
   */
  z.object({ t: z.literal("snapshot"), sessionId, offset: z.number().int().nonnegative(), data: bytes }),
  z.object({ t: z.literal("session.exited"), sessionId, exitCode: z.number().int().nullable() }),
  /**
   * Which way a session runs. `switching`: the hand-over is under way (it takes a second or two). `reason`: why the last
   * `session.mode.set` was refused or failed (the mode is then unchanged).
   */
  z.object({ t: z.literal("session.mode"), sessionId, mode: SessionMode, switching: z.boolean(), reason: z.string().max(500).optional() }),
  /**
   * Everything a phone needs to draw a chat it just opened. `historyBefore`: the conversation from before the chat started is
   * read with `session.transcript` using this as `before` (null: there is none). `approvals`: can the agent ask for
   * permission in the chat, or does it fall back to its own permission settings (`note` says what that means).
   */
  z.object({
    t: z.literal("chat.state"),
    sessionId,
    entries: z.array(ChatEntry).max(300),
    streaming: z.object({ id: z.string().max(100), text: z.string().max(60_000) }).nullable(),
    status: ChatStatus,
    detail: z.string().max(500).optional(),
    historyBefore: z.number().int().nonnegative().nullable(),
    approvals: z.enum(["chat", "agent"]),
    note: z.string().max(500).optional(),
    /** "Allow everything in this chat" is on. */
    allowAll: z.boolean().optional(),
  }),
  z.object({ t: z.literal("chat.allowAll"), sessionId, on: z.boolean() }),
  /** More of the agent's reply as it is written (append `text` to the message `id`). */
  z.object({ t: z.literal("chat.delta"), sessionId, id: z.string().max(100), text: z.string().max(60_000) }),
  /** A finished message (replaces the streamed text with the same `id`), or something the person sent. */
  z.object({ t: z.literal("chat.message"), sessionId, entry: ChatEntry }),
  /** A tool row, new or updated (same `id`). */
  z.object({ t: z.literal("chat.tool"), sessionId, entry: ChatEntry }),
  /** An approval card, new or updated (same `id`). */
  z.object({ t: z.literal("chat.approval"), sessionId, entry: ChatEntry }),
  z.object({ t: z.literal("chat.status"), sessionId, status: ChatStatus, detail: z.string().max(500).optional() }),
  /** The agent finished its turn (`ok: false`: it stopped on an error, or was interrupted; `message` says which). */
  z.object({ t: z.literal("chat.result"), sessionId, ok: z.boolean(), message: z.string().max(1000).optional() }),
  z.object({ t: z.literal("error"), sessionId: sessionId.optional(), eventId: z.string().max(64).optional(), code: z.string(), message: z.string(), feature: z.string().max(64).optional() }),
]);
export type Message = z.infer<typeof Message>;
